import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';

// ALWAYS use relative URL - same-origin requests only
const API = '/api';

const PaymentSuccess = ({ user: propsUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [user, setUser] = useState(propsUser);
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
          withCredentials: true
        });

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(userRes.data);
          toast.success('Payment successful! Your plan has been upgraded.');
        } else if (attempts < 5) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Payment status check failed:', error);
        if (attempts < 5) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts, navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar user={user} />

      <main className="flex-1 py-24 px-6">
        <div className="container mx-auto max-w-2xl">
          {status === 'checking' && (
            <Card data-testid="payment-checking">
              <CardContent className="pt-12 pb-12 text-center">
                <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-6" />
                <h2 className="font-heading text-2xl font-bold mb-2">Processing Payment...</h2>
                <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
              </CardContent>
            </Card>
          )}

          {status === 'success' && (
            <Card data-testid="payment-success">
              <CardContent className="pt-12 pb-12 text-center">
                <CheckCircle className="h-16 w-16 text-risk-low mx-auto mb-6" />
                <h2 className="font-heading text-3xl font-bold mb-2">Payment Successful!</h2>
                <p className="text-muted-foreground mb-8">Your plan has been upgraded. Start scanning ads for compliance.</p>
                <Button size="lg" onClick={() => navigate('/dashboard')} data-testid="goto-dashboard-btn">
                  Go to Dashboard
                </Button>
              </CardContent>
            </Card>
          )}

          {status === 'pending' && (
            <Card data-testid="payment-pending">
              <CardContent className="pt-12 pb-12 text-center">
                <Loader2 className="h-16 w-16 text-muted-foreground mx-auto mb-6" />
                <h2 className="font-heading text-2xl font-bold mb-2">Payment Pending</h2>
                <p className="text-muted-foreground mb-8">
                  Your payment is being processed. This may take a few minutes. Check your email for confirmation.
                </p>
                <Button onClick={() => navigate('/dashboard')} data-testid="goto-dashboard-pending-btn">
                  Return to Dashboard
                </Button>
              </CardContent>
            </Card>
          )}

          {status === 'error' && (
            <Card data-testid="payment-error">
              <CardContent className="pt-12 pb-12 text-center">
                <h2 className="font-heading text-2xl font-bold mb-2">Payment Error</h2>
                <p className="text-muted-foreground mb-8">
                  There was an issue processing your payment. Please contact support if the issue persists.
                </p>
                <Button onClick={() => navigate('/pricing')} data-testid="retry-payment-btn">
                  Try Again
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentSuccess;
